import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import productLogo from "../assets/images/country-seal.jpg";
import mlgLogo from "../assets/images/mlg-logo.png";
import { setTokens } from "../api/apiClient";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import { BASE_URL } from "../config/api";
import { showErrorToast } from "../utils/toast";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Forgot password modal state
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const handleForgotPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSendingReset(true);
    const baseUrl = `${window.location.origin}/reset-password/create-password`; // backend appends /reset-password?token=... to this
    try {
      const response = await fetch(
        `${BASE_URL}/Password/send-reset-password-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: forgotEmail, baseUrl }),
        },
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to send reset email.");
      }
      setForgotSuccess(true);
    } catch (err) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsSendingReset(false);
    }
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setForgotEmail("");
    setForgotSuccess(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(event.currentTarget);
    const username = String(formData.get("username") ?? "").trim();
    const password = String(formData.get("password") ?? "").trim();

    try {
      const response = await fetch(`${BASE_URL}/Auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        throw new Error("Invalid username or password.");
      }

      const data = (await response.json()) as {
        accessToken?: string;
        refreshToken?: string;
        username?: string;
        firstName?: string;
        roleName?: string;
        rolePermissions?: string[];
        profileImagePath?: string;
      };

      setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });

      if (data.username) {
        localStorage.setItem("twdc_username", data.username);
      }
      if (data.firstName) {
        localStorage.setItem("twdc_first_name", data.firstName);
      }
      if (data.roleName) {
        localStorage.setItem("twdc_role_name", data.roleName);
      }
      if (data.rolePermissions) {
        localStorage.setItem(
          "twdc_role_permissions",
          JSON.stringify(data.rolePermissions),
        );
      }
      if (data.profileImagePath) {
        localStorage.setItem("twdc_user_image", data.profileImagePath);
      }

      // Redirect based on dashboard permission
      if (HasPermission(ModuleId.Dashboard, ApplicationPermissionId.View)) {
        navigate("/dashboard");
      } else {
        navigate("/profile");
      }
    } catch (error) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {showForgotModal && (
        <div
          className="forgot-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="forgot-title"
          onClick={closeForgotModal}
        >
          <div className="forgot-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="forgot-modal-close"
              type="button"
              onClick={closeForgotModal}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M18 6 6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <h2 id="forgot-title">Reset Password</h2>
            <p>
              Enter your email address and we'll send you a link to reset your
              password.
            </p>
            {forgotSuccess ? (
              <div className="forgot-success">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M20 6 9 17l-5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Reset link sent! Check your email inbox.</span>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <div className="form-field">
                  <input
                    id="forgot-email"
                    type="email"
                    placeholder=" "
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    autoFocus
                  />
                  <label htmlFor="forgot-email">Email Address</label>
                </div>
                <button
                  className="primary-btn"
                  type="submit"
                  disabled={isSendingReset}
                >
                  {isSendingReset ? "Sending..." : "Send Reset Link"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
      <main className="auth-body">
        <section className="auth-shell">
          <div className="auth-hero">
            <div className="hero-overlay" />
            <div className="hero-content">
              <div className="hero-logo">
                <img src={mlgLogo} alt="Ministry of Local Government" />
              </div>
              <h1>Ministry of Local Government</h1>
              <br />
              <div className="hero-footnote">Secure Administration Portal</div>
            </div>
          </div>
          <div className="auth-panel">
            <div className="product-logo">
              <img className="logo-desktop" src={productLogo} alt="Seal" />
              <img
                className="logo-mobile"
                src={mlgLogo}
                alt="Ministry of Local Government"
              />
            </div>
            <div className="auth-panel-main">
              <span className="auth-description">
                Traditional Marriage &amp; Divorce Certificates
              </span>
              <form className="login-form" onSubmit={handleSubmit}>
                <div className="form-field">
                  <input
                    id="username"
                    name="username"
                    type="text"
                    placeholder=" "
                    required
                  />
                  <label htmlFor="username">Username or Email</label>
                </div>

                <button
                  type="button"
                  className="forgot-link"
                  onClick={() => setShowForgotModal(true)}
                >
                  Forgot Password?
                </button>

                <div className="form-field">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder=" "
                    required
                  />
                  <label htmlFor="password">Password</label>
                  <button
                    className="toggle-password"
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 5c5.05 0 9.27 3.11 11 7-1.01 2.26-2.81 4.13-5.07 5.35l-1.6-1.6A7.9 7.9 0 0 0 19.7 12c-1.45-2.6-4.35-4.5-7.7-4.5-1.15 0-2.25.22-3.25.62L7.2 6.57A11.6 11.6 0 0 1 12 5Zm-9.3 7a11.3 11.3 0 0 0 4.39 4.7l-1.6 1.6A13.1 13.1 0 0 1 1 12a13.2 13.2 0 0 1 3.54-4.9l1.6 1.6A11.2 11.2 0 0 0 2.7 12Zm9.3-3.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-.36 1.55l-4.7-4.7c.5-.22 1.03-.35 1.56-.35Zm-3.5 3.5c0-.53.13-1.05.35-1.55l4.7 4.7c-.5.22-1.03.35-1.55.35A3.5 3.5 0 0 1 8.5 12Zm-4.32-8.32 16.14 16.14-1.41 1.41-2.24-2.24A11.7 11.7 0 0 1 12 19c-5.05 0-9.27-3.11-11-7a12.7 12.7 0 0 1 4.03-5.1L2.76 4.63 4.18 3.68Z" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 5c5.05 0 9.27 3.11 11 7-1.73 3.89-5.95 7-11 7S2.73 15.89 1 12c1.73-3.89 5.95-7 11-7Zm0 2.5c-3.35 0-6.25 1.9-7.7 4.5 1.45 2.6 4.35 4.5 7.7 4.5s6.25-1.9 7.7-4.5c-1.45-2.6-4.35-4.5-7.7-4.5Zm0 1.5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 1.8a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Z" />
                      </svg>
                    )}
                  </button>
                </div>

                <button className="primary-btn" type="submit">
                  {isSubmitting ? "Signing In..." : "Sign In"}
                </button>
                <a className="register-link" href="#">
                  Register for Certificate
                </a>
              </form>
            </div>
            <div className="login-powered">
              Powered by{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.mwetana.com.lr"
              >
                MWETANA
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Login;
